import { useState } from "react";
import { Trophy, Medal, Star, ShareNetwork } from "@phosphor-icons/react";
import ShareBadgeDialog from "./ShareBadgeDialog";

/** Phase 244 — Visual ribbon for a Supplier's "Top Supplier of [Month]" badge.
 * Renders an inline, colour-tiered chip (Gold / Silver / Bronze) showing the
 * tier label and period. Use `compact` for table rows, default for cards.
 *
 * Props:
 *   badge       — the badge object returned by the API (tier_rank, tier_label,
 *                 tier_color, tier_border, tier_emoji, period_label, completed,
 *                 rating_avg, sla_grade)
 *   compact     — boolean; renders a tiny inline pill instead of full ribbon
 *   showShare   — boolean; if true, adds a one-tap "Share" button on the
 *                 ribbon that opens the social-share dialog (Phase 245)
 *   className   — optional extra classes
 */
export default function SupplierBadgeRibbon({ badge, compact = false, showShare = false, className = "" }) {
  const [shareOpen, setShareOpen] = useState(false);

  if (!badge) return null;
  const color = badge.tier_color || "#D4AF37";
  const border = badge.tier_border || "#B8860B";
  const Icon = badge.tier_rank === 1 ? Trophy : badge.tier_rank === 2 ? Medal : Star;

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white shadow-sm ${className}`}
        style={{ background: color, border: `1px solid ${border}` }}
        data-testid={`supplier-badge-compact-${badge.id || badge.tier_rank}`}
        title={`${badge.tier_label} — ${badge.period_label}`}
      >
        <Icon size={10} weight="fill" />
        <span className="truncate max-w-[90px]">{badge.tier_label}</span>
      </span>
    );
  }

  return (
    <>
    <div
      className={`flex items-center gap-3 rounded-xl p-3 shadow-md text-white ${className}`}
      style={{ background: `linear-gradient(135deg, ${color}, ${border})`, border: `2px solid ${border}` }}
      data-testid={`supplier-badge-ribbon-${badge.id || badge.tier_rank}`}
    >
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl"
        style={{ background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.35)" }}
      >
        <Icon size={24} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-90">
          {badge.tier_label} Supplier · #{badge.tier_rank}
        </p>
        <p className="text-base font-bold leading-tight truncate">
          {badge.vendor_name || `Best Supplier · ${badge.period_label}`}
        </p>
        {badge.vendor_name && (
          <p className="text-[11px] opacity-90 leading-tight">Best Supplier · {badge.period_label}</p>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] opacity-90">
          {badge.completed != null && <span>{badge.completed} completion{badge.completed === 1 ? "" : "s"}</span>}
          {badge.rating_avg != null && <span>★ {badge.rating_avg}/5 ({badge.rating_count})</span>}
          {badge.sla_grade && <span>SLA: {badge.sla_grade}</span>}
        </div>
      </div>
      {showShare && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShareOpen(true); }}
          className="shrink-0 ml-auto flex flex-col items-center justify-center gap-0.5 px-2.5 py-1.5 rounded-md transition-colors active:scale-95"
          style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)" }}
          aria-label="Share badge"
          data-testid={`supplier-badge-share-btn-${badge.id || badge.tier_rank}`}
        >
          <ShareNetwork size={16} weight="fill" />
          <span className="text-[9px] font-bold uppercase tracking-wider">Share</span>
        </button>
      )}
    </div>
    {showShare && (
      <ShareBadgeDialog badge={badge} open={shareOpen} onOpenChange={setShareOpen} />
    )}
    </>
  );
}
